import type { TacticsBranch } from '@4cm/core/tactics';
import { HudChip } from './HudChip';

interface BranchPickerProps {
  /** Branches available at (or relevant to) the current keyframe. */
  branches: TacticsBranch[];
  /** Currently selected branch — undefined ⇒ main sequence. */
  activeBranchId?: string;
  /** Fired when the coach picks a branch. id=undefined returns to main. */
  onSelect: (id?: string) => void;
}

/**
 * Phase 2 — chip row that lets the coach swap between the main play
 * and any authored "if X" branches. Always renders a "Main" chip so
 * the coach can step back out of a branch without rebuilding the
 * scrubber state; the alternate branches follow as labelled chips
 * carrying the coach-authored trigger string.
 *
 * Renders nothing when there are no branches — keeps the HUD clean
 * on layouts that don't fork.
 */
export function BranchPicker({
  branches,
  activeBranchId,
  onSelect,
}: BranchPickerProps) {
  if (!branches || branches.length === 0) return null;
  const isMain = !activeBranchId;
  return (
    <div
      className="thud__branches"
      role="radiogroup"
      aria-label="Tactical branch"
      data-testid="branch-picker"
    >
      <HudChip
        active={isMain}
        ariaLabel="Show the main sequence"
        onClick={() => onSelect(undefined)}
      >
        Main
      </HudChip>
      {branches.map((b) => (
        <HudChip
          key={b.id}
          active={activeBranchId === b.id}
          team="away"
          ariaLabel={`Show branch: ${b.trigger}`}
          onClick={() => onSelect(b.id)}
        >
          {b.trigger}
        </HudChip>
      ))}
    </div>
  );
}
